// src/pages/InvoiceManagement.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, FileText, AlertTriangle, Eye, DollarSign, Calendar, ArrowLeft, X, Download, Building2, Printer, Tag, Truck, RotateCcw, ArrowRightLeft } from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import ReturnExchange from './ReturnExchange';

const InvoiceManagement = ({ defaultMode = null, defaultStatus = '', onNavigate, isStatementView = false }) => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentView, setCurrentView] = useState('list');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [showReturnExchange, setShowReturnExchange] = useState(false);
  const [invoiceReturns, setInvoiceReturns] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [showTodayOnly, setShowTodayOnly] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Company state
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const { user } = useAuth();
  
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const invoiceStatuses = [
    { value: 'UNPAID', label: 'Unpaid', color: 'text-red-600 bg-red-100' },
    { value: 'PAID', label: 'Paid', color: 'text-green-600 bg-green-100' },
    { value: 'PARTIALLY_PAID', label: 'Partially Paid', color: 'text-yellow-600 bg-yellow-100' },
    { value: 'OVERDUE', label: 'Overdue', color: 'text-red-600 bg-red-100' },
    { value: 'REFUNDED', label: 'Refunded', color: 'text-purple-600 bg-purple-100' },
    { value: 'PARTIALLY_REFUNDED', label: 'Partially Refunded', color: 'text-orange-600 bg-orange-100' },
  ];

  const paymentMethods = ['CASH', 'PAYBILL', 'TILL'];

  const [invoiceForm, setInvoiceForm] = useState({
    customerId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    taxRate: 0,
    discountAmount: 0,
    notes: '',
    items: [],
    companyId: ''
  });

  const [itemForm, setItemForm] = useState({
    productId: '',
    quantity: 1,
    unitPrice: 0,
    discountAmount: 0,
    description: ''
  });

  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    customerType: 'INDIVIDUAL'
  });

  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'CASH',
    reference: '',
    notes: ''
  });

  // Delivery label state
  const [showDeliveryLabel, setShowDeliveryLabel] = useState(false);
  const [deliveryLabelInvoice, setDeliveryLabelInvoice] = useState(null);
  const [deliveryForm, setDeliveryForm] = useState({
    to: '',
    location: '',
    mobile: '',
    notes: '',
    fragile: false
  });

  // Update-customer (convert a "Walking" customer to the real one) state
  const [showUpdateCustomer, setShowUpdateCustomer] = useState(false);
  const [updateCustomerForm, setUpdateCustomerForm] = useState({ name: '', phone: '', email: '' });
  const [updatingCustomer, setUpdatingCustomer] = useState(false);

  const isToday = (dateString) => {
    const today = new Date().toDateString();
    const invoiceDate = new Date(dateString).toDateString();
    return today === invoiceDate;
  };

  const isInDateRange = (dateString) => {
    if (!dateRange.startDate && !dateRange.endDate) return true;
    const invoiceDate = new Date(dateString);
    const start = dateRange.startDate ? new Date(dateRange.startDate) : null;
    const end = dateRange.endDate ? new Date(dateRange.endDate) : null;
    if (start && end) return invoiceDate >= start && invoiceDate <= end;
    if (start) return invoiceDate >= start;
    if (end) return invoiceDate <= end;
    return true;
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Reload data when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadInvoices();
      loadCustomers();
      loadProducts();
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (defaultStatus) setSelectedStatus(defaultStatus);
    if (defaultMode === 'create') setTimeout(() => handleCreate(), 500);
    if (isStatementView) {
      setShowTodayOnly(false);
      setDateRange({ startDate: '', endDate: '' });
    }
  }, [defaultMode, defaultStatus, isStatementView]);

  useEffect(() => {
    if (customerSearch) {
      const filtered = customers.filter(customer =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.email?.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [customerSearch, customers]);

  useEffect(() => {
    if (productSearch) {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.sku?.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.description?.toLowerCase().includes(productSearch.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [productSearch, products]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search') && !event.target.closest('.product-search')) {
        setShowCustomerDropdown(false);
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const filterInvoices = () => {
      let filtered = [...invoices];
      if (searchTerm) {
        filtered = filtered.filter(invoice =>
          invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          invoice.customer?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          invoice.notes?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      if (isStatementView) {
        filtered = filtered.filter(invoice => 
          invoice.status === 'UNPAID' || invoice.status === 'PARTIALLY_PAID'
        );
      } else {
        if (selectedStatus) {
          filtered = filtered.filter(invoice => invoice.status === selectedStatus);
        } else {
          filtered = filtered.filter(invoice => 
            invoice.status === 'UNPAID' || invoice.status === 'PARTIALLY_PAID'
          );
        }
      }
      if (!isStatementView) {
        if (showTodayOnly) {
          filtered = filtered.filter(invoice => isToday(invoice.invoiceDate));
        } else {
          filtered = filtered.filter(invoice => isInDateRange(invoice.invoiceDate));
        }
      }
      setFilteredInvoices(filtered);
      setCurrentPage(1);
    };
    filterInvoices();
  }, [invoices, searchTerm, selectedStatus, showTodayOnly, dateRange, isStatementView]);

  const loadCompanies = async () => {
    try {
      const data = await apiService.getAllCompanies();
      setCompanies(data);
      const savedCompanyId = localStorage.getItem('selectedCompanyId');
      if (savedCompanyId && data.find(c => c.id === parseInt(savedCompanyId))) {
        setSelectedCompanyId(savedCompanyId);
      } else if (data.length > 0) {
        setSelectedCompanyId(data[0].id.toString());
        localStorage.setItem('selectedCompanyId', data[0].id.toString());
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    localStorage.setItem('selectedCompanyId', companyId);
    setCurrentPage(1);
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = selectedCompanyId
        ? await apiService.getInvoicesByCompany(selectedCompanyId)
        : await apiService.getAllInvoices();
      setInvoices(data);
    } catch (error) {
      setError('Failed to load invoices');
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await apiService.getActiveCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      // Load products scoped to selected company
      const data = selectedCompanyId
        ? await apiService.getActiveProductsByCompany(selectedCompanyId)
        : await apiService.getActiveProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  // Load returns when viewing an invoice
  useEffect(() => {
    if (currentView === 'view' && selectedInvoice) {
      loadInvoiceReturns(selectedInvoice.id);
    }
  }, [currentView, selectedInvoice]);

  const loadInvoiceReturns = async (invoiceId) => {
    try {
      const returns = await apiService.getReturnsByInvoice(invoiceId);
      setInvoiceReturns(returns);
    } catch (error) {
      console.error('Error loading returns:', error);
      setInvoiceReturns([]);
    }
  };

  const calculateTotals = () => {
    return filteredInvoices.reduce((totals, invoice) => {
      totals.totalAmount += parseFloat(invoice.totalAmount || 0);
      totals.paidAmount += parseFloat(invoice.paidAmount || 0);
      totals.balanceAmount += parseFloat(invoice.balanceAmount || 0);
      return totals;
    }, { totalAmount: 0, paidAmount: 0, balanceAmount: 0 });
  };

  const groupInvoicesByCustomer = () => {
    const grouped = {};
    filteredInvoices.forEach(invoice => {
      const customerId = invoice.customer.id;
      if (!grouped[customerId]) {
        grouped[customerId] = {
          customer: invoice.customer,
          invoices: [],
          totalAmount: 0,
          paidAmount: 0,
          balanceAmount: 0
        };
      }
      grouped[customerId].invoices.push(invoice);
      grouped[customerId].totalAmount += parseFloat(invoice.totalAmount || 0);
      grouped[customerId].paidAmount += parseFloat(invoice.paidAmount || 0);
      grouped[customerId].balanceAmount += parseFloat(invoice.balanceAmount || 0);
    });
    return grouped;
  };

  // ============================
  // THERMAL RECEIPT / INVOICE PRINTING
  // ============================
  const printThermalReceipt = (invoice, payment) => {
    const company = companies.find(c => c.id === parseInt(selectedCompanyId));
    const companyName = company?.name || 'Company';
    const companyAddress = company?.address || '';
    const companyPhone = company?.phone || '';
    const companyEmail = company?.email || '';

    // Determine if this is a RECEIPT (fully paid) or INVOICE (unpaid/partial)
    const totalAmount = parseFloat(invoice.totalAmount || 0);
    const paidAmount = parseFloat(invoice.paidAmount || 0) + parseFloat(payment?.amount || 0);
    const isFullyPaid = paidAmount >= totalAmount && totalAmount > 0;
    const isInvoice = !isFullyPaid;
    const docTitle = isInvoice ? 'INVOICE' : 'SALE RECEIPT';
    const docLabel = isInvoice ? 'Invoice #' : 'Receipt #';
    const balanceAfterPayment = Math.max(0, totalAmount - paidAmount);

    const fmtAmt = (amount) => {
      const num = parseFloat(amount || 0);
      return num.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const truncate = (str, maxLen) => {
      if (!str) return '-';
      return str.length > maxLen ? str.substring(0, maxLen) : str;
    };

    const receiptContent = `
      <html>
      <head>
        <title>${docTitle} - ${invoice.invoiceNumber}</title>
        <style>
          @page { 
            margin: 0; 
            size: 80mm auto; 
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', 'Lucida Console', monospace;
            font-size: 12px;
            font-weight: 900;
            -webkit-print-color-adjust: exact;
            -webkit-text-stroke: 0.4px #000;
            text-rendering: geometricPrecision;
            line-height: 1.35;
            width: 42ch;
            max-width: 62mm;
            margin: 2mm auto 2mm 2mm;
            padding: 0;
            color: #000;
            overflow: hidden;
          }
          .center { text-align: center; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          .dashed {
            border: none;
            border-top: 1px dashed #000;
            margin: 3px 0;
          }
          .solid {
            border: none;
            border-top: 2px solid #000;
            margin: 3px 0;
          }
          .company-name {
            font-size: 14px;
            font-weight: 900;
            letter-spacing: 1px;
            -webkit-text-stroke: 0.5px #000;
          }
          .company-detail {
            font-size: 11px;
          }
          .receipt-title {
            font-size: 13px;
            font-weight: 900;
            letter-spacing: 2px;
            padding: 2px 0;
            -webkit-text-stroke: 0.5px #000;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            padding: 1px 0;
          }
          .info-label {
            flex-shrink: 0;
          }
          .info-value {
            text-align: right;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 58%;
          }
          .items-header {
            display: flex;
            font-weight: 900;
            font-size: 11px;
            padding: 2px 0;
          }
          .item-row {
            display: flex;
            align-items: flex-start;
            font-size: 11px;
            padding: 2px 0;
          }
          .col-name { flex: 1; overflow: hidden; word-wrap: break-word; overflow-wrap: break-word; white-space: normal; padding-right: 4px; }
          .col-qty { width: 24px; text-align: center; flex-shrink: 0; padding: 0 4px; }
          .col-price { width: 56px; text-align: right; flex-shrink: 0; padding-left: 6px; }
          .col-total { width: 58px; text-align: right; flex-shrink: 0; padding-left: 6px; }
          .summary-row {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            padding: 1px 0;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            font-weight: 900;
            padding: 3px 0;
            -webkit-text-stroke: 0.5px #000;
          }
          .paid-row {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            font-weight: 900;
            padding: 2px 0;
            -webkit-text-stroke: 0.5px #000;
          }
          .footer {
            font-size: 10px;
            text-align: center;
            padding: 2px 0;
          }
          .spacer { height: 4px; }
          @media print {
            body { width: 42ch; max-width: 62mm; margin: 2mm auto 2mm 2mm; }
          }
        </style>
      </head>
      <body>
        <!-- Company Header -->
        <div class="center">
          <div class="company-name">${companyName}</div>
          ${companyPhone ? `<div class="company-detail">Tel: ${companyPhone}</div>` : ''}
          ${companyAddress ? `<div class="company-detail">${companyAddress}</div>` : ''}
        </div>
        
        <hr class="dashed" />
        
        <div class="center">
          <div class="receipt-title">${docTitle}</div>
        </div>
        
        <hr class="dashed" />
        
        <!-- Transaction Info -->
        <div class="info-row">
          <span class="info-label">${docLabel}:</span>
          <span class="info-value">${invoice.invoiceNumber}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Date:</span>
          <span class="info-value">${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Customer:</span>
          <span class="info-value">${truncate(invoice.customer?.name || 'Walk-in', 22)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Served by:</span>
          <span class="info-value">${truncate(user?.name || user?.username || '-', 22)}</span>
        </div>
        
        <hr class="solid" />
        
        <!-- Items Header -->
        <div class="items-header">
          <span class="col-name">Item</span>
          <span class="col-qty">Qty</span>
          <span class="col-price">Price</span>
          <span class="col-total">Total</span>
        </div>
        
        <hr class="dashed" />
        
        <!-- Items -->
        ${(invoice.items || []).map(item => {
          const qty = parseFloat(item.quantity || 0);
          const price = parseFloat(item.unitPrice || 0);
          const lineTotal = qty * price;
          const desc = item.product?.description || item.description || '';
          return `
        <div class="item-row">
          <span class="col-name">${item.product?.name || '-'}${desc ? `<br/><span style="font-size:10px;font-weight:700;">${desc}</span>` : ''}</span>
          <span class="col-qty">${qty.toFixed(0)}</span>
          <span class="col-price">${fmtAmt(price)}</span>
          <span class="col-total">${fmtAmt(lineTotal)}</span>
        </div>`;
        }).join('')}
        
        <hr class="solid" />
        
        <!-- Totals -->
        <div class="summary-row">
          <span>Subtotal:</span>
          <span>Ksh ${fmtAmt(invoice.subtotal || calculateLocalSubtotal(invoice))}</span>
        </div>
        ${parseFloat(invoice.taxAmount || 0) > 0 ? `
        <div class="summary-row">
          <span>Tax (${invoice.taxRate || 0}%):</span>
          <span>Ksh ${fmtAmt(invoice.taxAmount)}</span>
        </div>
        ` : ''}
        ${parseFloat(invoice.discountAmount || 0) > 0 ? `
        <div class="summary-row">
          <span>Discount:</span>
          <span>-Ksh ${fmtAmt(invoice.discountAmount)}</span>
        </div>
        ` : ''}
        
        <div class="total-row">
          <span>TOTAL:</span>
          <span>Ksh ${fmtAmt(invoice.totalAmount || calculateLocalTotal(invoice))}</span>
        </div>
        
        <hr class="dashed" />
        
        <!-- Payment -->
        ${isInvoice ? `
        <div class="paid-row">
          <span>PAID:</span>
          <span>Ksh ${fmtAmt(paidAmount)}</span>
        </div>
        <div class="total-row" style="color:#000; border-top: 1px dashed #000; padding-top: 4px; margin-top: 2px;">
          <span>BALANCE DUE:</span>
          <span>Ksh ${fmtAmt(balanceAfterPayment)}</span>
        </div>
        ` : `
        <div class="paid-row">
          <span>PAID:</span>
          <span>Ksh ${fmtAmt(payment?.amount || invoice.totalAmount)}</span>
        </div>
        ${payment && parseFloat(payment.amount) > parseFloat(invoice.totalAmount) ? `
        <div class="summary-row">
          <span>Change:</span>
          <span>Ksh ${fmtAmt(parseFloat(payment.amount) - parseFloat(invoice.totalAmount))}</span>
        </div>
        ` : ''}
        `}
        
        <hr class="dashed" />
        
        <!-- Footer -->
        <div class="footer">
          ${isInvoice ? `
          <div style="font-weight:900; font-size:11px;">Payment Details</div>
          <div style="font-size:12px; font-weight:900;">Paybill Number: 542542</div>
          <div style="font-size:12px; font-weight:900;">Account Number: 5306993</div>
          ` : `
          ${payment?.paymentMethod ? `<div>Payment: ${payment.paymentMethod}</div>` : ''}
          ${payment?.reference ? `<div>Ref: ${payment.reference}</div>` : ''}
          `}
          <div class="spacer"></div>
          <div>Thank you for shopping with us</div>
        </div>
        
        <hr class="dashed" />
        
        <div class="footer">
          Printed: ${new Date().toLocaleString('en-GB')}
        </div>
        <div class="spacer"></div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=300,height=600');
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // ============================
  // DELIVERY LABEL PRINTING
  // ============================
  const printDeliveryLabel = (invoice, delivery) => {
    const company = companies.find(c => c.id === parseInt(selectedCompanyId));
    const companyName = company?.name || 'Company';
    const companyPhone = company?.phone || '';
    const companyAddress = company?.address || '';

    const labelContent = `
      <html>
      <head>
        <title>Delivery Label - ${invoice.invoiceNumber}</title>
        <style>
          @page { 
            margin: 0; 
            size: 80mm auto; 
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', 'Lucida Console', monospace;
            font-size: 16px;
            font-weight: 900;
            -webkit-print-color-adjust: exact;
            -webkit-text-stroke: 0.5px #000;
            text-rendering: geometricPrecision;
            line-height: 1.4;
            width: 42ch;
            max-width: 62mm;
            margin: 2mm auto 2mm 2mm;
            padding: 0;
            color: #000;
            overflow: hidden;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .dashed {
            border: none;
            border-top: 1px dashed #000;
            margin: 4px 0;
          }
          .solid {
            border: none;
            border-top: 2px solid #000;
            margin: 4px 0;
          }
          .header-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }
          .company-name {
            font-size: 18px;
            font-weight: 900;
            letter-spacing: 1px;
            -webkit-text-stroke: 0.6px #000;
          }
          .label-title {
            font-size: 17px;
            font-weight: 900;
            letter-spacing: 2px;
            padding: 2px 0;
            -webkit-text-stroke: 0.6px #000;
          }
          .fragile-badge {
            border: 3px solid #000;
            padding: 4px 10px;
            font-size: 18px;
            font-weight: 900;
            letter-spacing: 2px;
            -webkit-text-stroke: 0.8px #000;
            text-align: center;
            margin-bottom: 2px;
          }
          .field-row {
            padding: 4px 0;
          }
          .field-label {
            font-size: 13px;
            font-weight: 900;
            letter-spacing: 1px;
            text-transform: uppercase;
            color: #000;
          }
          .field-value {
            font-size: 18px;
            font-weight: 900;
            -webkit-text-stroke: 0.6px #000;
            padding: 2px 0;
            word-wrap: break-word;
          }
          .items-section {
            font-size: 14px;
          }
          .item-row {
            display: flex;
            justify-content: space-between;
            padding: 1px 0;
          }
          .footer {
            font-size: 13px;
            text-align: center;
            padding: 3px 0;
          }
          .spacer { height: 4px; }
          @media print {
            body { width: 42ch; max-width: 62mm; margin: 2mm auto 2mm 2mm; }
          }
        </style>
      </head>
      <body>
        <!-- FRAGILE badge top-right if active -->
        ${delivery.fragile ? `
        <div style="text-align: right; margin-bottom: 2px;">
          <div class="fragile-badge" style="display: inline-block;">⚠ FRAGILE ⚠</div>
        </div>
        ` : ''}

        <!-- To (Recipient) -->
        <div class="field-row">
          <div class="field-label">To</div>
          <div class="field-value">${delivery.to || '-'}</div>
        </div>

        <div class="field-row">
          <div class="field-label">Mobile</div>
          <div class="field-value">${delivery.mobile || '-'}</div>
        </div>

        <div class="field-row">
          <div class="field-label">Location</div>
          <div class="field-value" style="white-space: pre-line;">${delivery.location || '-'}</div>
        </div>

        <hr class="dashed" />

        <!-- Items with SKU -->
        <div style="font-size:13px; font-weight:900; letter-spacing:1px; text-transform:uppercase; padding:2px 0;">Items</div>
        ${(invoice.items || []).map(item => {
          const desc = item.product?.description || item.description || '';
          return `
        <div style="font-size:14px; font-weight:900; padding:2px 0; -webkit-text-stroke:0.4px #000;">
          ${item.product?.name || '-'} x${parseFloat(item.quantity || 0).toFixed(0)}${desc ? ` <span style="font-size:12px;">${desc}</span>` : ''}
        </div>`;
        }).join('')}

        <hr class="dashed" />

        ${delivery.fragile ? `
        <div class="center" style="padding: 4px 0;">
          <div style="font-size:18px; font-weight:900; letter-spacing:3px; -webkit-text-stroke:0.8px #000; border: 2px solid #000; padding: 5px;">
            ⚠ HANDLE WITH CARE ⚠
          </div>
        </div>
        <hr class="dashed" />
        ` : ''}

        <!-- From (Company) -->
        <div class="field-row">
          <div class="field-label">From</div>
          <div class="field-value">${companyName}</div>
          ${companyAddress ? `<div style="font-size:15px; font-weight:900; -webkit-text-stroke:0.4px #000;">${companyAddress}</div>` : ''}
          ${companyPhone ? `<div style="font-size:14px; font-weight:900; -webkit-text-stroke:0.4px #000;">Tel: ${companyPhone}</div>` : ''}
        </div>

        <hr class="dashed" />

        <!-- Footer -->
        <div class="footer">
          <div>${invoice.invoiceNumber} | ${new Date().toLocaleDateString('en-GB')}</div>
        </div>
        <div style="text-align:center; font-size:14px; font-weight:900; padding:4px 0 2px 0; -webkit-text-stroke:0.4px #000;">
          Thank you for shopping with us
        </div>
        <div class="spacer"></div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=300,height=600');
    printWindow.document.write(labelContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handlePrintDeliveryLabel = () => {
    if (!deliveryLabelInvoice) return;
    printDeliveryLabel(deliveryLabelInvoice, deliveryForm);
    setShowDeliveryLabel(false);
  };

  // Delivery Label Modal (reusable across views)
  const renderDeliveryLabelModal = () => {
    if (!showDeliveryLabel || !deliveryLabelInvoice) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <Tag className="w-5 h-5 text-violet-600" />
              <h3 className="text-lg font-semibold text-gray-900">Delivery Label</h3>
            </div>
            <button onClick={() => setShowDeliveryLabel(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Invoice Reference */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">Invoice</div>
              <div className="font-semibold text-gray-900">{deliveryLabelInvoice.invoiceNumber}</div>
              <div className="text-sm text-gray-600">{(deliveryLabelInvoice.items || []).length} item(s)</div>
            </div>

            {/* Fragile Toggle */}
            <div className="flex items-center justify-between p-3 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertTriangle className={`w-5 h-5 ${deliveryForm.fragile ? 'text-red-600' : 'text-gray-400'}`} />
                <span className={`font-medium ${deliveryForm.fragile ? 'text-red-700' : 'text-gray-700'}`}>
                  {deliveryForm.fragile ? 'FRAGILE ITEM' : 'Mark as Fragile'}
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={deliveryForm.fragile}
                  onChange={(e) => setDeliveryForm({...deliveryForm, fragile: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>

            {/* To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To (Recipient Name)</label>
              <input
                type="text"
                value={deliveryForm.to}
                onChange={(e) => setDeliveryForm({...deliveryForm, to: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="Recipient name"
              />
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
              <input
                type="text"
                value={deliveryForm.mobile}
                onChange={(e) => setDeliveryForm({...deliveryForm, mobile: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="Phone number"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <textarea
                rows={3}
                value={deliveryForm.location}
                onChange={(e) => setDeliveryForm({...deliveryForm, location: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="Delivery address / location (multi-line)"
              />
            </div>
          </div>

          <div className="flex space-x-3 p-4 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={handlePrintDeliveryLabel}
              className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Printer className="w-4 h-4" />
              <span>Print Label</span>
            </button>
            <button
              onClick={() => setShowDeliveryLabel(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const calculateLocalSubtotal = (invoice) => {
    return (invoice.items || []).reduce((sum, item) => {
      return sum + (parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0));
    }, 0);
  };

  const calculateLocalTotal = (invoice) => {
    const subtotal = calculateLocalSubtotal(invoice);
    const taxAmount = subtotal * (parseFloat(invoice.taxRate || 0) / 100);
    return subtotal + taxAmount - parseFloat(invoice.discountAmount || 0);
  };

  // ============================
  // PDF Statement Generation
  // ============================
  const generatePDF = (type = 'all', customerId = null) => {
    const totals = calculateTotals();
    const companyName = companies.find(c => c.id === parseInt(selectedCompanyId))?.name || '';
    let content = '';
    
    if (type === 'all') {
      const groupedInvoices = groupInvoicesByCustomer();
      content = `
        <html>
        <head>
          <title>Customer Statement - All</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .customer-section { margin-bottom: 30px; page-break-inside: avoid; }
            .customer-header { background-color: #f3f4f6; padding: 10px; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f9fafb; }
            .totals { font-weight: bold; background-color: #f3f4f6; }
            .grand-total { margin-top: 30px; font-size: 18px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${companyName} - Customer Statement Report</h1>
            <p>Unpaid and Partially Paid Invoices</p>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          ${Object.values(groupedInvoices).map(customerData => `
            <div class="customer-section">
              <div class="customer-header">
                <h2>${customerData.customer.name}</h2>
                <p>Email: ${customerData.customer.email || 'N/A'} | Phone: ${customerData.customer.phone || 'N/A'}</p>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Invoice #</th><th>Date</th><th>Due Date</th>
                    <th>Total Amount</th><th>Paid Amount</th><th>Balance</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${customerData.invoices.map(invoice => `
                    <tr>
                      <td>${invoice.invoiceNumber}</td>
                      <td>${new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                      <td>${new Date(invoice.dueDate).toLocaleDateString()}</td>
                      <td>${formatCurrency(invoice.totalAmount)}</td>
                      <td>${formatCurrency(invoice.paidAmount)}</td>
                      <td>${formatCurrency(invoice.balanceAmount)}</td>
                      <td>${invoice.status}</td>
                    </tr>
                  `).join('')}
                  <tr class="totals">
                    <td colspan="3">Customer Total</td>
                    <td>${formatCurrency(customerData.totalAmount)}</td>
                    <td>${formatCurrency(customerData.paidAmount)}</td>
                    <td>${formatCurrency(customerData.balanceAmount)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          `).join('')}
          <div class="grand-total">
            <p>Grand Total Amount: ${formatCurrency(totals.totalAmount)}</p>
            <p>Grand Total Paid: ${formatCurrency(totals.paidAmount)}</p>
            <p>Grand Total Balance: ${formatCurrency(totals.balanceAmount)}</p>
          </div>
        </body>
        </html>
      `;
    } else {
      const customer = customers.find(c => c.id === customerId);
      const customerInvoices = filteredInvoices.filter(inv => inv.customer.id === customerId);
      const customerTotals = customerInvoices.reduce((totals, invoice) => {
        totals.totalAmount += parseFloat(invoice.totalAmount || 0);
        totals.paidAmount += parseFloat(invoice.paidAmount || 0);
        totals.balanceAmount += parseFloat(invoice.balanceAmount || 0);
        return totals;
      }, { totalAmount: 0, paidAmount: 0, balanceAmount: 0 });
      
      content = `
        <html>
        <head>
          <title>Customer Statement - ${customer?.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f9fafb; }
            .totals { font-weight: bold; background-color: #f3f4f6; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${companyName} - Customer Statement</h1>
            <h2>${customer?.name}</h2>
            <p>Email: ${customer?.email || 'N/A'} | Phone: ${customer?.phone || 'N/A'}</p>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Invoice #</th><th>Date</th><th>Due Date</th>
                <th>Total Amount</th><th>Paid Amount</th><th>Balance</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${customerInvoices.map(invoice => `
                <tr>
                  <td>${invoice.invoiceNumber}</td>
                  <td>${new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                  <td>${new Date(invoice.dueDate).toLocaleDateString()}</td>
                  <td>${formatCurrency(invoice.totalAmount)}</td>
                  <td>${formatCurrency(invoice.paidAmount)}</td>
                  <td>${formatCurrency(invoice.balanceAmount)}</td>
                  <td>${invoice.status}</td>
                </tr>
              `).join('')}
              <tr class="totals">
                <td colspan="3">Total</td>
                <td>${formatCurrency(customerTotals.totalAmount)}</td>
                <td>${formatCurrency(customerTotals.paidAmount)}</td>
                <td>${formatCurrency(customerTotals.balanceAmount)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </body>
        </html>
      `;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const handleCreate = () => {
    setCurrentView('create');
    setSelectedInvoice(null);
    setInvoiceForm({
      customerId: '',
      customerName: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      taxRate: 0,
      discountAmount: 0,
      notes: '',
      items: [],
      paymentAmount: null,
      paymentMethod: 'CASH',
      paymentReference: '',
      paymentNotes: '',
      companyId: selectedCompanyId
    });
    setCustomerSearch('');
    setProductSearch('');
    setError('');
    setModalError('');
  };

  const handleView = async (invoice) => {
    setCurrentView('view');
    setSelectedInvoice(invoice);
    // Refetch the latest invoice so status, payments and customer are always current
    try {
      const fresh = await apiService.getInvoiceById(invoice.id);
      if (fresh) setSelectedInvoice(fresh);
    } catch (err) {
      console.error('Failed to refresh invoice details:', err);
    }
  };

  const handleEdit = (invoice) => {
    setCurrentView('edit');
    setSelectedInvoice(invoice);
    
    const normalizedItems = (invoice.items || []).map(item => ({
      id: item.id,
      productId: item.productId || item.product?.id,
      product: item.product,
      quantity: parseFloat(item.quantity),
      unitPrice: parseFloat(item.unitPrice),
      discountAmount: parseFloat(item.discountAmount) || 0,
      description: item.description || item.product?.description || '',
      lineTotal: (parseFloat(item.unitPrice) - parseFloat(item.discountAmount || 0)) * parseFloat(item.quantity)
    }));
    
    setInvoiceForm({
      customerId: invoice.customer.id,
      customerName: invoice.customer.name,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      taxRate: invoice.taxRate || 0,
      discountAmount: invoice.discountAmount || 0,
      notes: invoice.notes || '',
      items: normalizedItems,
      companyId: invoice.companyId || selectedCompanyId
    });
    setCustomerSearch(invoice.customer.name);
    setError('');
    setModalError('');
  };

  const handleAddPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setCurrentView('payment');
    setPaymentForm({
      amount: invoice.balanceAmount || 0,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'CASH',
      reference: '',
      notes: ''
    });
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedInvoice(null);
    setError('');
    setModalError('');
  };

  const handleOpenUpdateCustomer = () => {
    setUpdateCustomerForm({
      name: selectedInvoice?.customer?.name || '',
      phone: selectedInvoice?.customer?.phone || '',
      email: selectedInvoice?.customer?.email || ''
    });
    setShowUpdateCustomer(true);
  };

  const handleSubmitUpdateCustomer = async (e) => {
    e.preventDefault();
    if (!updateCustomerForm.name.trim()) {
      setError('Customer name is required');
      return;
    }
    setUpdatingCustomer(true);
    setError('');
    try {
      const updated = await apiService.reassignInvoiceCustomer(selectedInvoice.id, {
        name: updateCustomerForm.name.trim(),
        phone: updateCustomerForm.phone.trim(),
        email: updateCustomerForm.email.trim()
      });
      if (updated) setSelectedInvoice(updated);
      setShowUpdateCustomer(false);
      loadInvoices();
    } catch (error) {
      setError(error.message || 'Failed to update customer');
    } finally {
      setUpdatingCustomer(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setInvoiceForm({
      ...invoiceForm,
      customerId: customer.id,
      customerName: customer.name
    });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const handleSelectProduct = (product) => {
    setItemForm({
      ...itemForm,
      productId: product.id,
      productName: product.name,
      unitPrice: product.price
    });
    setProductSearch(`${product.name} - ${product.sku || ''}`);
    setShowProductDropdown(false);
  };

  const handleAddItem = () => {
    const selectedProduct = products.find(p => p.id === parseInt(itemForm.productId));
    if (!selectedProduct) return;

    const totalStock = selectedProduct.quantityInStock || 0;
    const shopStock = selectedProduct.quantityInShop || 0;
    const storeStock = totalStock - shopStock;
    const requiredQty = parseFloat(itemForm.quantity);

    // Block if total stock is 0 (nothing in shop or store)
    if (requiredQty > totalStock) {
      setModalError(`Insufficient stock for ${selectedProduct.name}. Total Available: ${totalStock} (Shop: ${shopStock}, Store: ${storeStock}), Required: ${requiredQty}`);
      return;
    }

    // Warn if shop doesn't have enough but store does
    if (requiredQty > shopStock && storeStock > 0) {
      const shortfall = requiredQty - shopStock;
      const proceed = window.confirm(
        `⚠️ Low Shop Stock!\n\n` +
        `"${selectedProduct.name}" only has ${shopStock} in shop.\n` +
        `${shortfall} will need to come from store/warehouse (${storeStock} available there).\n\n` +
        `Do you want to proceed with this sale?`
      );
      if (!proceed) return;
    }

    setModalError('');

    const newItem = {
      productId: selectedProduct.id,
      product: selectedProduct,
      quantity: parseFloat(itemForm.quantity),
      unitPrice: parseFloat(itemForm.unitPrice || selectedProduct.price),
      discountAmount: parseFloat(itemForm.discountAmount || 0),
      description: itemForm.description || selectedProduct.description,
      lineTotal: (parseFloat(itemForm.unitPrice || selectedProduct.price) * parseFloat(itemForm.quantity || 0)) - parseFloat(itemForm.discountAmount || 0)
    };

    setInvoiceForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setItemForm({
      productId: '',
      quantity: 1,
      unitPrice: 0,
      discountAmount: 0,
      description: ''
    });
    setProductSearch(''); 
    setShowProductDropdown(false);
  };

  const handleRemoveItem = (index) => {
    setInvoiceForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const canEditInvoice = (invoiceDate, userRole) => {
    const today = new Date().toDateString();
    const invoiceDateString = new Date(invoiceDate).toDateString();
    const isDatePassed = new Date(invoiceDateString) < new Date(today);
    return !isDatePassed || userRole === 'ADMIN';
  };

  const handleSubmitInvoice = async (e) => {
    e.preventDefault();
    setError('');
    setModalError('');
    
    if (!invoiceForm.items || invoiceForm.items.length === 0) {
      setModalError('Cannot save invoice without any items. Please add at least one item to the invoice.');
      return;
    }
    
    if (!invoiceForm.customerId) {
      setModalError('Please select a customer for this invoice.');
      return;
    }
    
    try {
      const invoiceData = {
        ...invoiceForm,
        customerId: parseInt(invoiceForm.customerId),
        taxRate: parseFloat(invoiceForm.taxRate),
        discountAmount: parseFloat(invoiceForm.discountAmount),
        companyId: parseInt(invoiceForm.companyId || selectedCompanyId),
        items: invoiceForm.items.map(item => ({
          ...(item.id && { id: item.id }),
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          discountAmount: parseFloat(item.discountAmount || 0),
          description: item.description || ''
        }))
      };

      let createdInvoice;
      if (currentView === 'create') {
        createdInvoice = await apiService.createInvoice(invoiceData);
      } else {
        createdInvoice = await apiService.updateInvoice(selectedInvoice.id, invoiceData);
      }

      // Auto-print receipt (if fully paid) or invoice (if unpaid/partial)
      if (currentView === 'create' && total > 0) {
        // Build a print-ready invoice object from the form data
        const receiptInvoice = {
          ...createdInvoice,
          invoiceNumber: createdInvoice?.invoiceNumber || 'NEW',
          customer: customers.find(c => c.id === parseInt(invoiceForm.customerId)),
          items: invoiceForm.items,
          subtotal: subtotal,
          taxRate: invoiceForm.taxRate,
          taxAmount: taxAmount,
          discountAmount: invoiceForm.discountAmount,
          totalAmount: total,
          paidAmount: 0 // Will be added by payment param below
        };
        const receiptPayment = {
          amount: parseFloat(invoiceForm.paymentAmount || 0),
          paymentMethod: invoiceForm.paymentMethod,
          reference: invoiceForm.paymentReference || ''
        };
        printThermalReceipt(receiptInvoice, receiptPayment);
      }

      handleBackToList();
      loadInvoices();
    } catch (error) {
      setError(error.message || 'Failed to save invoice');
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const paymentData = {
        invoiceId: selectedInvoice.id,
        amount: parseFloat(paymentForm.amount),
        paymentDate: paymentForm.paymentDate,
        paymentMethod: paymentForm.paymentMethod,
        reference: paymentForm.reference,
        notes: paymentForm.notes
      };

      await apiService.createPayment(paymentData);
      
      // Print thermal receipt when payment completes the invoice balance
      const remainingBalance = parseFloat(selectedInvoice.balanceAmount) - parseFloat(paymentForm.amount);
      if (remainingBalance <= 0) {
        printThermalReceipt(selectedInvoice, paymentData);
      }

      handleBackToList();
      loadInvoices();
    } catch (error) {
      setError(error.message || 'Failed to record payment');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const newCustomer = await apiService.createCustomer(customerForm);
      setCustomers([...customers, newCustomer]);
      handleSelectCustomer(newCustomer);
      setCustomerForm({
        name: '', email: '', phone: '', address: '', city: '', state: '', country: '', customerType: 'INDIVIDUAL'
      });
      setShowCustomerModal(false);
    } catch (error) {
      setError('Failed to create customer: ' + error.message);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await apiService.deleteInvoice(invoiceId);
        loadInvoices();
      } catch (error) {
        setError('Failed to delete invoice');
      }
    }
  };

  const getStatusConfig = (status) => {
    return invoiceStatuses.find(s => s.value === status) || invoiceStatuses[0];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const calculateInvoiceTotal = () => {
    const subtotal = invoiceForm.items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity || 0);
      const unitPrice = parseFloat(item.unitPrice || 0);
      const discountPerUnit = parseFloat(item.discountAmount || 0);
      const lineTotal = (parseFloat(unitPrice) * parseFloat(quantity || 0)) - parseFloat(discountPerUnit || 0);
      return sum + lineTotal;
    }, 0);
    const taxAmount = subtotal * (parseFloat(invoiceForm.taxRate || 0) / 100);
    const total = subtotal + taxAmount - parseFloat(invoiceForm.discountAmount || 0);
    return { subtotal, taxAmount, total };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const { subtotal, taxAmount, total } = calculateInvoiceTotal();
  const totals = calculateTotals();

  // Return/Exchange view (intercept before other views)
  if (showReturnExchange && selectedInvoice) {
    return (
      <ReturnExchange
        invoice={selectedInvoice}
        companies={companies}
        selectedCompanyId={selectedCompanyId}
        products={products}
        onClose={() => { setShowReturnExchange(false); handleBackToList(); }}
        onComplete={() => loadInvoices()}
      />
    );
  }

  // Invoice List View
  if (currentView === 'list') {
    return (
      <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isStatementView ? 'Customer Statement' : 'Invoice Management'}
            </h1>
            <p className="text-gray-600">
              {isStatementView ? 'View unpaid and partially paid invoices for all customers' : 'Create and manage invoices for your customers'}
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-2">
            {isStatementView && (
              <button
                onClick={() => generatePDF('all')}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download All PDF</span>
              </button>
            )}
            {!isStatementView && (
              <button
                onClick={handleCreate}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>New Invoice</span>
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0"><FileText className="h-6 lg:h-8 w-6 lg:w-8 text-indigo-600" /></div>
              <div className="ml-3 lg:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">Total Invoices</dt>
                  <dd className="text-sm lg:text-lg font-medium text-gray-900">{filteredInvoices.length}</dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0"><Calendar className="h-6 lg:h-8 w-6 lg:w-8 text-green-600" /></div>
              <div className="ml-3 lg:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">Paid</dt>
                  <dd className="text-sm lg:text-lg font-medium text-gray-900">{filteredInvoices.filter(i => i.status === 'PAID').length}</dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0"><Calendar className="h-6 lg:h-8 w-6 lg:w-8 text-red-600" /></div>
              <div className="ml-3 lg:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">Overdue</dt>
                  <dd className="text-sm lg:text-lg font-medium text-gray-900">{filteredInvoices.filter(i => i.status === 'OVERDUE').length}</dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0"><Calendar className="h-6 lg:h-8 w-6 lg:w-8 text-yellow-600" /></div>
              <div className="ml-3 lg:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs lg:text-sm font-medium text-gray-500 truncate">Pending</dt>
                  <dd className="text-sm lg:text-lg font-medium text-gray-900">{filteredInvoices.filter(i => ['SENT', 'PARTIALLY_PAID'].includes(i.status)).length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Totals Summary */}
        {user?.role === 'ADMIN' && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm border p-4 lg:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Summary Totals</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{formatCurrency(totals.totalAmount)}</div>
                <div className="text-sm text-gray-600">Total Amount</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{formatCurrency(totals.paidAmount)}</div>
                <div className="text-sm text-gray-600">Paid Amount</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{formatCurrency(totals.balanceAmount)}</div>
                <div className="text-sm text-gray-600">Balance Amount</div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {!isStatementView && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
              {/* Company Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                <select
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search invoices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  {invoiceStatuses.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
                <select
                  value={showTodayOnly ? 'today' : 'range'}
                  onChange={(e) => {
                    if (e.target.value === 'today') {
                      setShowTodayOnly(true);
                      setDateRange({ startDate: '', endDate: '' });
                    } else {
                      setShowTodayOnly(false);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="today">Today Only</option>
                  <option value="range">Date Range</option>
                </select>
              </div>

              <div>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedStatus('');
                    setShowTodayOnly(true);
                    setDateRange({ startDate: '', endDate: '' });
                  }}
                  className="mt-6 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {!showTodayOnly && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input type="date" value={dateRange.startDate}
                    onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input type="date" value={dateRange.endDate}
                    onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Statement view search */}
        {isStatementView && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                <select
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text" placeholder="Search by customer or invoice number..."
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <button onClick={() => setSearchTerm('')}
                  className="mt-6 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors w-full"
                >
                  Clear Search
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoices Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Customer</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Date / Due</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentInvoices.map((invoice) => {
                  const statusConfig = getStatusConfig(invoice.status);
                  const grossTotal = (invoice.items || []).reduce((s, it) => s + parseFloat(it.unitPrice || 0) * parseFloat(it.quantity || 0), 0);
                  const totalDiscount = (invoice.items || []).reduce((s, it) => s + parseFloat(it.discountAmount || 0), 0) + parseFloat(invoice.discountAmount || 0);
                  const isFullDiscount = totalDiscount > 0 && grossTotal > 0 && totalDiscount >= grossTotal;
                  return (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-3 lg:px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 lg:h-10 lg:w-10">
                            <div className="h-8 w-8 lg:h-10 lg:w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <FileText className="h-4 w-4 lg:h-5 lg:w-5 text-indigo-600" />
                            </div>
                          </div>
                          <div className="ml-2 lg:ml-4">
                            <div className="text-xs lg:text-sm font-medium text-gray-900">{invoice.invoiceNumber}</div>
                            <div className="text-xs text-gray-500 lg:hidden">{invoice.customer?.name}</div>
                            <div className="text-xs text-gray-500">{invoice.items?.length || 0} items</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 lg:px-6 py-4 hidden lg:table-cell">
                        <div className="text-sm font-medium text-gray-900">{invoice.customer?.name}</div>
                        <div className="text-sm text-gray-500">{invoice.customer?.email}</div>
                      </td>
                      <td className="px-3 lg:px-6 py-4 text-xs lg:text-sm text-gray-900 hidden md:table-cell">
                        <div>Date: {formatDate(invoice.invoiceDate)}</div>
                        <div className="text-gray-500">Due: {formatDate(invoice.dueDate)}</div>
                      </td>
                      <td className="px-3 lg:px-6 py-4 text-xs lg:text-sm text-gray-900">
                        <div className="font-medium">{formatCurrency(invoice.totalAmount)}</div>
                        {invoice.paidAmount > 0 && (
                          <div className="text-green-600 text-xs">Paid: {formatCurrency(invoice.paidAmount)}</div>
                        )}
                        {invoice.balanceAmount > 0 && (
                          <div className="text-red-600 text-xs">Bal: {formatCurrency(invoice.balanceAmount)}</div>
                        )}
                      </td>
                      <td className="px-3 lg:px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        {totalDiscount > 0 && (
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isFullDiscount ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {isFullDiscount ? 'Full Discount' : 'Discounted'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 lg:px-6 py-4 text-sm font-medium">
                        <div className="flex items-center space-x-1 lg:space-x-2">
                          <button onClick={() => handleView(invoice)} className="text-indigo-600 hover:text-indigo-900 p-1" title="View Invoice">
                            <Eye className="w-4 h-4" />
                          </button>
                          {/* Print Receipt/Invoice button */}
                          <button
                            onClick={() => {
                              const lastPayment = invoice.payments?.length > 0 ? invoice.payments[invoice.payments.length - 1] : null;
                              printThermalReceipt(invoice, {
                                amount: lastPayment?.amount || 0,
                                paymentMethod: lastPayment?.paymentMethod || 'N/A',
                                reference: lastPayment?.reference || ''
                              });
                            }}
                            className={`p-1 ${invoice.status === 'PAID' ? 'text-gray-600 hover:text-gray-900' : 'text-blue-600 hover:text-blue-900'}`}
                            title={invoice.status === 'PAID' ? 'Print Receipt' : 'Print Invoice'}
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          {/* Delivery Label button */}
                          <button
                            onClick={() => {
                              setDeliveryLabelInvoice(invoice);
                              setDeliveryForm({
                                to: invoice.customer?.name || '',
                                location: '',
                                mobile: invoice.customer?.phone || '',
                                notes: '',
                                fragile: false
                              });
                              setShowDeliveryLabel(true);
                            }}
                            className="text-violet-600 hover:text-violet-900 p-1"
                            title="Delivery Label"
                          >
                            <Tag className="w-4 h-4" />
                          </button>
                          {!isStatementView && invoice.status !== 'PAID' && canEditInvoice(invoice.invoiceDate, user?.role) && (
                            <button onClick={() => handleEdit(invoice)} className="text-blue-600 hover:text-blue-900 p-1" title="Edit Invoice">
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          {!isStatementView && (invoice.status === 'UNPAID' || invoice.status === 'PARTIALLY_PAID') && (
                            <button onClick={() => handleAddPayment(invoice)} className="text-green-600 hover:text-green-900 p-1" title="Add Payment">
                              <DollarSign className="w-4 h-4" />
                            </button>
                          )}
                          {isStatementView && (
                            <button onClick={() => generatePDF('customer', invoice.customer.id)} className="text-green-600 hover:text-green-900 p-1" title="Download Customer PDF">
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          {!isStatementView && user?.role === 'ADMIN' && (
                            <button onClick={() => handleDelete(invoice.id)} className="text-red-600 hover:text-red-900 p-1" title="Delete Invoice">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))} disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Previous</button>
                  <button onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))} disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Next</button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(indexOfLastItem, filteredInvoices.length)}</span> of{' '}
                      <span className="font-medium">{filteredInvoices.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))} disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        const isCurrentPage = pageNumber === currentPage;
                        if (pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)) {
                          return (
                            <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${isCurrentPage ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}>{pageNumber}</button>
                          );
                        } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                          return <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                        }
                        return null;
                      })}
                      <button onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))} disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Next</button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>

          {currentInvoices.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {isStatementView ? "No unpaid or partially paid invoices found." : showTodayOnly ? "No invoices created today." : "Try adjusting your filters."}
              </p>
            </div>
          )}
        </div>
      </div>
      {renderDeliveryLabelModal()}
      </>
    );
  }

  // Create/Edit Invoice Form View
  if (currentView === 'create' || currentView === 'edit') {
    return (
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={handleBackToList} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
              {currentView === 'create' ? 'Create New Invoice' : 'Edit Invoice'}
            </h1>
            <p className="text-gray-600 text-sm lg:text-base">
              {currentView === 'create' ? 'Fill in the details to create a new invoice' : 'Update invoice information'}
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}
        {modalError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{modalError}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmitInvoice} className="space-y-6">
          {/* Invoice Header */}
          <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Information</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              {/* Company Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                <select
                  required
                  value={invoiceForm.companyId}
                  onChange={(e) => {
                    const newCompanyId = e.target.value;
                    setInvoiceForm({...invoiceForm, companyId: newCompanyId, items: []});
                    // Reload products for the new company
                    setSelectedCompanyId(newCompanyId);
                    localStorage.setItem('selectedCompanyId', newCompanyId);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                <div className="flex gap-2">
                  <div className="relative customer-search flex-1">
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                    {showCustomerDropdown && filteredCustomers.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                        {filteredCustomers.slice(0, 10).map(customer => (
                          <div key={customer.id} onClick={() => handleSelectCustomer(customer)}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0">
                            <div className="font-medium text-sm">{customer.name}</div>
                            <div className="text-xs text-gray-500">{customer.email}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => setShowCustomerModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add</span>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date *</label>
                  <input type="date" required value={invoiceForm.invoiceDate}
                    onChange={(e) => setInvoiceForm({...invoiceForm, invoiceDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                  <input type="date" required value={invoiceForm.dueDate}
                    onChange={(e) => setInvoiceForm({...invoiceForm, dueDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                </div>
              </div>
            </div>
          </div>

          {/* Add Items Section */}
          <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Add Items
              {invoiceForm.companyId && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (Products from: {companies.find(c => c.id === parseInt(invoiceForm.companyId))?.name})
                </span>
              )}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                <div className="relative product-search">
                  <input type="text" placeholder="Search products..."
                    value={productSearch}
                    onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                  {showProductDropdown && filteredProducts.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                      {filteredProducts.slice(0, 10).map(product => (
                        <div key={product.id} onClick={() => handleSelectProduct(product)}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0">
                          <div className="font-medium text-sm">{product.name}</div>
                          <div className="text-xs text-gray-500">
                            {product.sku} - {formatCurrency(product.price)} 
                            (Total: {product.quantityInStock} | <span className="text-blue-600">Shop: {product.quantityInShop || 0}</span> | <span className="text-amber-600">Store: {(product.quantityInStock || 0) - (product.quantityInShop || 0)}</span>)
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input type="number" min="0.01" step="0.01" value={itemForm.quantity}
                  onChange={(e) => setItemForm({...itemForm, quantity: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                <input type="number" step="0.01" value={itemForm.unitPrice}
                  onChange={(e) => setItemForm({...itemForm, unitPrice: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
                <input type="number" step="0.01" value={itemForm.discountAmount}
                  onChange={(e) => setItemForm({...itemForm, discountAmount: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              </div>
              <div className="flex items-end">
                <button type="button" onClick={handleAddItem} disabled={!itemForm.productId}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                  Add Item
                </button>
              </div>
            </div>
          </div>

          {/* Invoice Items */}
          {invoiceForm.items.length > 0 ? (
            <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Items</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoiceForm.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-900">{item.product?.name}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{item.product?.description || item.product?.unit || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{parseFloat(item.quantity).toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.unitPrice)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.discountAmount)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {formatCurrency((parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0)) - parseFloat(item.discountAmount || 0))}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {user?.role === 'ADMIN' && (
                            <button type="button" onClick={() => handleRemoveItem(index)} className="text-red-600 hover:text-red-900">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Items Added</h3>
              <p className="text-gray-600 mb-4">Add at least one item to create an invoice.</p>
              <p className="text-sm text-orange-600 font-medium">⚠️ You cannot save an invoice without any items</p>
            </div>
          )}

          {/* Invoice Footer */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea rows={3} value={invoiceForm.notes}
                      onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Additional notes..." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                      <input type="number" step="0.01" value={invoiceForm.taxRate}
                        onChange={(e) => setInvoiceForm({...invoiceForm, taxRate: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Discount Amount</label>
                      <input type="number" step="0.01" value={invoiceForm.discountAmount}
                        onChange={(e) => setInvoiceForm({...invoiceForm, discountAmount: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Payment (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                    <input type="number" step="0.01" value={invoiceForm.paymentAmount || ''}
                      onChange={(e) => {
                        const paymentAmount = parseFloat(e.target.value) || 0;
                        const currentTotal = total;
                        let autoStatus = 'UNPAID';
                        if (paymentAmount > 0) {
                          autoStatus = paymentAmount >= currentTotal ? 'PAID' : 'PARTIALLY_PAID';
                        }
                        setInvoiceForm({ ...invoiceForm, paymentAmount, invoiceStatus: autoStatus });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="0.00" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select value={invoiceForm.paymentMethod || 'CASH'}
                      onChange={(e) => setInvoiceForm({...invoiceForm, paymentMethod: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      {paymentMethods.map(method => (
                        <option key={method} value={method}>{method.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Status</label>
                    <select value={invoiceForm.invoiceStatus || 'SENT'}
                      onChange={(e) => setInvoiceForm({...invoiceForm, invoiceStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                      <option value="UNPAID">Unpaid</option>
                      <option value="PARTIALLY_PAID">Partially Paid</option>
                      <option value="PAID">Fully Paid</option>
                    </select>
                  </div>
                </div>
                {/* Print note */}
                {total > 0 && (
                  <div className={`mt-3 p-3 rounded-lg border ${parseFloat(invoiceForm.paymentAmount || 0) >= total ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className={`flex items-center text-sm ${parseFloat(invoiceForm.paymentAmount || 0) >= total ? 'text-green-700' : 'text-blue-700'}`}>
                      <Printer className="w-4 h-4 mr-2" />
                      {parseFloat(invoiceForm.paymentAmount || 0) >= total
                        ? 'A thermal receipt will be printed automatically when this fully-paid invoice is saved.'
                        : 'A thermal invoice will be printed automatically when saved (showing balance due).'
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Subtotal:</span>
                  <span className="text-sm font-medium">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Tax ({invoiceForm.taxRate}%):</span>
                  <span className="text-sm font-medium">{formatCurrency(taxAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Discount:</span>
                  <span className="text-sm font-medium">-{formatCurrency(invoiceForm.discountAmount)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-medium">Total:</span>
                  <span className="font-medium text-lg">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button type="submit" disabled={!invoiceForm.items || invoiceForm.items.length === 0 || !invoiceForm.customerId}
              className="flex-1 sm:flex-none px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
              {currentView === 'create' ? 'Create Invoice' : 'Update Invoice'}
            </button>
            <button type="button" onClick={handleBackToList}
              className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        </form>

        {/* Customer Creation Modal */}
        {showCustomerModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <form onSubmit={handleCreateCustomer}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Add New Customer</h3>
                      <button type="button" onClick={() => setShowCustomerModal(false)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                          <input type="text" required value={customerForm.name}
                            onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input type="email" value={customerForm.email}
                            onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input type="tel" value={customerForm.phone}
                            onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Customer Type</label>
                          <select value={customerForm.customerType}
                            onChange={(e) => setCustomerForm({...customerForm, customerType: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                            <option value="INDIVIDUAL">Individual</option>
                            <option value="BUSINESS">Business</option>
                            <option value="NON_PROFIT">Non-Profit</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 sm:ml-3 sm:w-auto sm:text-sm">
                      Create Customer
                    </button>
                    <button type="button" onClick={() => setShowCustomerModal(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // View Invoice Details
  if (currentView === 'view') {
    return (
      <>
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={handleBackToList} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Invoice Details</h1>
              <p className="text-gray-600 text-sm lg:text-base">{selectedInvoice?.invoiceNumber}</p>
            </div>
          </div>
          {/* Print Receipt/Invoice Button */}
          <button
            onClick={() => {
              const lastPayment = selectedInvoice?.payments?.length > 0 ? selectedInvoice.payments[selectedInvoice.payments.length - 1] : null;
              printThermalReceipt(selectedInvoice, {
                amount: lastPayment?.amount || 0,
                paymentMethod: lastPayment?.paymentMethod || 'N/A',
                reference: lastPayment?.reference || ''
              });
            }}
            className={`px-4 py-2 ${selectedInvoice?.status === 'PAID' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors flex items-center space-x-2`}
          >
            <Printer className="w-4 h-4" />
            <span>{selectedInvoice?.status === 'PAID' ? 'Print Receipt' : 'Print Invoice'}</span>
          </button>
          {/* Delivery Label Button */}
          <button
            onClick={() => {
              setDeliveryLabelInvoice(selectedInvoice);
              setDeliveryForm({
                to: selectedInvoice?.customer?.name || '',
                location: '',
                mobile: selectedInvoice?.customer?.phone || '',
                notes: '',
                fragile: false
              });
              setShowDeliveryLabel(true);
            }}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center space-x-2"
          >
            <Tag className="w-4 h-4" />
            <span>Delivery Label</span>
          </button>
          {/* Return & Exchange Buttons - Admin only, UNPAID/PAID/PARTIALLY_PAID invoices */}
          {(selectedInvoice?.status === 'PAID' || selectedInvoice?.status === 'PARTIALLY_PAID' || selectedInvoice?.status === 'UNPAID') && user?.role === 'ADMIN' && (
            <>
              <button
                onClick={() => setShowReturnExchange(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Return</span>
              </button>
              <button
                onClick={() => setShowReturnExchange(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <ArrowRightLeft className="w-4 h-4" />
                <span>Exchange</span>
              </button>
            </>
          )}
          {/* Update Customer - only on a fully paid invoice (e.g. convert Walking customer to the real one) */}
          {selectedInvoice?.status === 'PAID' && (
            <button
              onClick={handleOpenUpdateCustomer}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2"
            >
              <Edit className="w-4 h-4" />
              <span>Update Customer</span>
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
              <p className="text-sm text-gray-600">{selectedInvoice?.customer?.name}</p>
              <p className="text-sm text-gray-600">{selectedInvoice?.customer?.email}</p>
              <p className="text-sm text-gray-600">{selectedInvoice?.customer?.phone}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Invoice Information</h4>
              <p className="text-sm text-gray-600">Company: {companies.find(c => c.id === selectedInvoice?.companyId)?.name || '-'}</p>
              <p className="text-sm text-gray-600">Date: {formatDate(selectedInvoice?.invoiceDate)}</p>
              <p className="text-sm text-gray-600">Due: {formatDate(selectedInvoice?.dueDate)}</p>
              <p className="text-sm text-gray-600">Status: {getStatusConfig(selectedInvoice?.status).label}</p>
            </div>
          </div>

          {/* Additional Notes */}
          {selectedInvoice?.notes && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-1">Additional Notes</h4>
              <p className="text-sm text-gray-700 whitespace-pre-line">{selectedInvoice.notes}</p>
            </div>
          )}

          {/* Invoice Items */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-2">Invoice Items</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedInvoice?.items?.map((item, index) => {
                    const lineGross = parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0);
                    const lineDiscount = parseFloat(item.discountAmount || 0);
                    const isFullDiscount = lineDiscount > 0 && lineGross > 0 && lineDiscount >= lineGross;
                    return (
                    <tr key={index}>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {item.product?.name}
                        {lineDiscount > 0 && (
                          <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isFullDiscount ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {isFullDiscount ? 'Full Discount' : 'Discounted'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">{item.product?.description || item.product?.unit || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{item.quantity}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.unitPrice)}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{lineDiscount > 0 ? `-${formatCurrency(lineDiscount)}` : '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.lineTotal)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="border-t pt-4 mb-6">
            <div className="flex justify-end">
              <div className="w-full sm:w-64">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Subtotal:</span>
                  <span className="text-sm font-medium">{formatCurrency(selectedInvoice?.subtotal)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Tax:</span>
                  <span className="text-sm font-medium">{formatCurrency(selectedInvoice?.taxAmount)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Discount:</span>
                  <span className="text-sm font-medium">-{formatCurrency((selectedInvoice?.items?.reduce((s, it) => s + parseFloat(it.discountAmount || 0), 0) || 0) + parseFloat(selectedInvoice?.discountAmount || 0))}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-medium">Total:</span>
                  <span className="font-medium">{formatCurrency(selectedInvoice?.totalAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Paid:</span>
                  <span className="text-sm font-medium text-green-600">{formatCurrency(selectedInvoice?.paidAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Balance:</span>
                  <span className="text-sm font-medium text-red-600">{formatCurrency(selectedInvoice?.balanceAmount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payments */}
          {selectedInvoice?.payments?.length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-2">Payment History</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedInvoice.payments.map((payment, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-900">{formatDate(payment.paymentDate)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(payment.amount)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{payment.paymentMethod}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{payment.reference || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{payment.notes || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{payment.status || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Return / Exchange History */}
          {invoiceReturns.length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-2">Return / Exchange History</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Return #</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Return Value</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Net Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Processed By</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoiceReturns.map((ret, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm font-medium text-indigo-600">{ret.returnNumber}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{new Date(ret.returnDate).toLocaleDateString()}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            ret.returnType === 'EXCHANGE' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {ret.returnType === 'EXCHANGE' ? 'Exchange' : 'Return'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(ret.returnSubtotal)}</td>
                        <td className="px-4 py-2 text-sm font-medium">
                          <span className={ret.netAmount > 0 ? 'text-green-600' : ret.netAmount < 0 ? 'text-red-600' : 'text-gray-600'}>
                            {ret.netAmount > 0 ? 'Refund: ' : ret.netAmount < 0 ? 'Top-up: ' : ''}
                            {formatCurrency(Math.abs(ret.netAmount))}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700 whitespace-pre-line max-w-xs">{ret.reason || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{ret.processedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      {renderDeliveryLabelModal()}
      {showUpdateCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">Update Customer</h3>
              <button onClick={() => setShowUpdateCustomer(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitUpdateCustomer} className="p-6 space-y-4">
              <p className="text-sm text-gray-500">
                Reassign this invoice to the actual customer. A new customer record is created; the original placeholder is left unchanged.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                <input type="text" required value={updateCustomerForm.name}
                  onChange={(e) => setUpdateCustomerForm({ ...updateCustomerForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Actual customer name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="text" value={updateCustomerForm.phone}
                  onChange={(e) => setUpdateCustomerForm({ ...updateCustomerForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Phone (optional)" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={updateCustomerForm.email}
                  onChange={(e) => setUpdateCustomerForm({ ...updateCustomerForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Email (optional)" />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => setShowUpdateCustomer(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={updatingCustomer}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                  {updatingCustomer ? 'Updating...' : 'Update Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>
    );
  }

  // Payment Form
  if (currentView === 'payment') {
    return (
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={handleBackToList} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Add Payment</h1>
            <p className="text-gray-600 text-sm lg:text-base">
              {selectedInvoice?.invoiceNumber} - Balance: {formatCurrency(selectedInvoice?.balanceAmount)}
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
          <form onSubmit={handleSubmitPayment} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount *</label>
                <input type="number" step="0.01" required max={selectedInvoice?.balanceAmount}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                <input type="date" required value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({...paymentForm, paymentDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
              <select required value={paymentForm.paymentMethod}
                onChange={(e) => setPaymentForm({...paymentForm, paymentMethod: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                {paymentMethods.map(method => (
                  <option key={method} value={method}>{method.replace('_', ' ')}</option>
                ))}
              </select>
            </div>

            {/* Receipt note for complete payment */}
            {parseFloat(paymentForm.amount || 0) >= parseFloat(selectedInvoice?.balanceAmount || 0) && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center text-green-700 text-sm">
                  <Printer className="w-4 h-4 mr-2" />
                  A thermal receipt will be printed automatically as this payment completes the invoice balance.
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
              <input type="text" value={paymentForm.reference}
                onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Check number, transaction ID, etc." />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea rows={3} value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Additional payment notes..." />
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <button type="submit" className="flex-1 sm:flex-none px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Record Payment
              </button>
              <button type="button" onClick={handleBackToList}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default InvoiceManagement;